import { useEffect, useRef } from "react";
import { motion, AnimatePresence, Variants } from "framer-motion"; // Variants 추가
import Card from "./Card";
import Button from "./Button";
import Text from "./Text";
import Flex from "./Flex";
import AlertStyle from "./css/Alert.module.css";
import IcoInfo from "../../assets/img/ico_info.png";
import IcoWarning from "../../assets/img/ico_warning.png";
import Spacer from "./Spacer";

type AlertProps = {
    type?: "info" | "warning" | "success" | "error";
    title?: string;
    text?: string;
    confirmText?: string;
    isOpen: boolean;
    onClose: () => void;
}

function matchTypeToIcon(type: AlertProps["type"]) {
    const style = {
        width: 24,
        height: 24
    }
    switch (type) {
        case "info":
            return <img src={IcoInfo} alt="info" style={style} />;
        default:
            return <img src={IcoWarning} alt="warning" style={style} />;
    }
}

const backdropVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
};

const alertVariants: Variants = {
    hidden: { 
        opacity: 0, 
        y: 50, 
        scale: 0.95 
    },
    visible: (type: string) => ({
        opacity: 1,
        y: 0,
        scale: 1,
        x: type === "warning" || type === "error" ? [0, -5, 5, -5, 5, -5, 5, -5, 5, 0] : 0,
        transition: {
            type: "spring" as const, 
            duration: 0.5,
            bounce: 0.3,
            x: { 
                delay: 0.2, 
                duration: 0.4, 
                type: "tween" as const
            } 
        }
    }),
    exit: { 
        opacity: 0, 
        y: 50, 
        scale: 0.95,
        transition: { duration: 0.2 } 
    }
};

function Alert({ type = "info", title = "알림", text = "", confirmText = "확인", isOpen, onClose }: AlertProps) {
    const backdropRef = useRef<HTMLDivElement>(null);
    const AlertIcon = matchTypeToIcon(type);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className={AlertStyle.backdrop}
                    ref={backdropRef}
                    variants={backdropVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    style={{ 
                        position: 'fixed', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)'
                    }}
                >
                    <motion.div
                        variants={alertVariants}
                        custom={type}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        style={{ outline: 'none', userSelect: 'none' }}
                    >
                        <Card
                            className={AlertStyle.alertContainer}
                            shadow={24}
                            borderRadius={16}
                            padding={24}
                            width={400}
                        >
                            <Flex flexDirection="column" gap={24}>
                                <Flex flexDirection="column" gap={12}>
                                    <Flex alignItems="center" gap={8}>
                                        {AlertIcon}
                                        <Text variant="h3" weight="semibold" size="18" color={type === 'warning' || type === 'error' ? "#F55555" : "black"}>
                                            {title}
                                        </Text>
                                    </Flex>
                                    <Spacer direction="vertical" size={2} />
                                    {text && (
                                        <Text textAlign="left" variant="p" size="16" weight="medium" color="#666" noWrap={false} style={{ lineHeight: "1.5rem", whiteSpace: "pre-wrap" }}>
                                            {text}
                                        </Text>
                                    )}
                                </Flex>
                                <Flex gap={8} justifyContent="end">
                                    <Button 
                                        variant="primary"
                                        onClick={onClose}
                                        height={30}
                                        borderRadius={8}
                                        style={{ fontSize: "17px", fontWeight: "500", minWidth: "85px" }}
                                    >
                                        <Text size={14}>확인</Text>
                                    </Button>
                                </Flex>
                            </Flex>
                        </Card>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default Alert;
export type { AlertProps };